import { ContentCopy } from '@mui/icons-material'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    SxProps,
    TextField,
    Theme,
    Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { EligibilityTransactionLog } from '../../../services/models/eligibility'
import { DentalMarketingPlan } from '../../../services/models/inurance-plan'
import { getDentalPlan } from '../../../services/productService'
import { fromHttpStatusCode, fromRelationshipCode, getStringValueOrDefault } from '../../../utils'
import { DataPanel } from '../../DataPanel'
import OneStepForm, { DataEntry, FormResult, FormValueType } from '../../OneStepForm'
import { DentalProductDetailsDialog } from './DentalProductDetailsDialog'
interface ViewEligibilityDetailsDialogProps {
    data?: EligibilityTransactionLog
    currentNode: 'provider' | 'payer'
    open: boolean
    onClose: () => void
    onMemberClick: (memberId: string, planYear: number) => void
}

const EligibilityDetailsDialog = (props: ViewEligibilityDetailsDialogProps) => {
    const { data, open, onClose, onMemberClick, currentNode } = props

    if (!data) {
        if (open) {
            onClose()
        }
        return <></>
    }

    const [formData, setFormData] = useState<{
        demographics: DataEntry[]
        address: DataEntry[]
        subscriber: DataEntry[]
        accumulator: DataEntry[]
        productBenefits: DataEntry[]
        product: DataEntry[]
        coverageStatus: DataEntry[]
        transactionLogs: DataEntry[]
        recordDetails: DataEntry[]
    }>({
        demographics: [],
        address: [],
        subscriber: [],
        accumulator: [],
        productBenefits: [],
        product: [],
        coverageStatus: [],
        transactionLogs: [],
        recordDetails: [],
    })
    const [formResults, setFormResults] = useState<{ [key: string]: FormResult }>({})
    const [submitDisabled, setSubmitDisabled] = useState(true)
    const [planDialogOpen, setPlanDialogOpen] = useState(false)
    const [planDialogData, setPlanDialogData] = useState<DentalMarketingPlan | null>(null)

    const { requestContent, responseContent } = data
    const patient =
        responseContent?.dependent ??
        responseContent?.subscriber ??
        requestContent?.payload?.dependent ??
        requestContent?.payload?.subscriber
    const plan = responseContent?.plan

    useEffect(() => {
        const fetchAndPopulate = async () => {
            if (plan?.id) {
                const { data } = await getDentalPlan(plan.id)
                setPlanDialogData(data.plan)
            }

            const recordDetailsData: DataEntry[] = [
                {
                    key: 'lastUpdated',
                    label: 'LastUpdated (GMT)',
                    type: FormValueType.DATE,
                    initialValue: data?.completedOn,
                    readOnly: true,
                },
                {
                    key: 'updatedBy',
                    label: 'Updated By',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(data.requestingUserId),
                    readOnly: true,
                },
                {
                    key: 'requestPayloadHash',
                    label: 'Hash of Eligibility Payload',
                    type: FormValueType.TEXT,
                    initialValue: `${getStringValueOrDefault(data?.requestHash)?.substring(
                        0,
                        50,
                    )}...`,
                    readOnly: true,
                    options: {
                        wordWrap: true,
                    },
                },
                {
                    key: 'anchorAddress',
                    label: 'Anchor Address',
                    type: FormValueType.TEXT,
                    initialValue: 'TBD',
                    readOnly: true,
                },
                {
                    key: 'priorRecordAddress',
                    label: 'Prior Record Address',
                    type: FormValueType.TEXT,
                    initialValue: 'TBD',
                    readOnly: true,
                },
            ]
            const transactionLogsData: DataEntry[] = [
                {
                    key: 'startedAt',
                    label: 'Started (GMT)',
                    type: FormValueType.DATE,
                    initialValue: data.createdOn,
                    readOnly: true,
                },
                {
                    key: 'ended',
                    label: 'Ended (GMT)',
                    type: FormValueType.DATE,
                    initialValue: data.completedOn,
                    readOnly: true,
                },
                {
                    key: 'duration',
                    label: 'Duration',
                    type: FormValueType.TEXT,
                    initialValue: `${data.duration} ms`,
                    readOnly: true,
                },
                {
                    key: 'httpStatusCode',
                    label: 'Http Response Code',
                    type: FormValueType.TEXT,
                    initialValue:
                        fromHttpStatusCode((data?.httpStatusCode ?? 0).toString()) ?? 'UNKNOWN',
                    readOnly: true,
                },
                {
                    key: 'sourceNode',
                    label: 'Source Node',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(data?.sourceNode),
                    readOnly: true,
                },
                {
                    key: 'destinationNode',
                    label: 'Destination Node',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(data?.destinationNode),
                    readOnly: true,
                },
                {
                    key: 'requestingUser',
                    label: 'Requesting User',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(data?.requestingUserId),
                    readOnly: true,
                },
                {
                    key: 'anchoringAddress',
                    label: 'Anchoring Address',
                    type: FormValueType.TEXT,
                    initialValue: 'TBD',
                    readOnly: true,
                },
                {
                    key: 'paymentSource',
                    label: 'Payment Source',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(data?.sourceNode),
                    readOnly: true,
                },
                {
                    key: 'paymentDestination',
                    label: 'Payment Destination',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(data?.destinationNode),
                    readOnly: true,
                },
                {
                    key: 'totalFees',
                    label: 'Total Fees (USD/Rhiza)',
                    type: FormValueType.TEXT,
                    initialValue: 'TBD',
                    readOnly: true,
                },
                {
                    key: 'feeTransactionAddress',
                    label: 'Address of Fee Transaction',
                    type: FormValueType.TEXT,
                    initialValue: 'TBD',
                    readOnly: true,
                },
            ]

            const demographicsData: DataEntry[] = [
                {
                    key: 'name',
                    label: 'Name',
                    type: FormValueType.TEXT,
                    initialValue: patient?.lastName
                        ? `${patient.lastName}, ${patient.firstName}`
                        : 'N/A',
                    readOnly: true,
                },
                {
                    key: 'dateOfBirth',
                    label: 'Date Of Birth',
                    type: FormValueType.DATE,
                    initialValue: patient?.dateOfBirth,
                    readOnly: true,
                },
                {
                    key: 'gender',
                    label: 'Gender',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(patient?.gender),
                    readOnly: true,
                },
                {
                    key: 'ssn',
                    label: 'SSN',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(patient?.ssn),
                    readOnly: true,
                },
            ]
            const addressData: DataEntry[] = [
                {
                    key: 'addressLine1',
                    label: 'Line 1',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(patient?.address?.addressLine1),
                    readOnly: true,
                },
                {
                    key: 'addressLine2',
                    label: 'Line 2',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(patient?.address?.addressLine2),
                    readOnly: true,
                },
                {
                    key: 'city',
                    label: 'City',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(patient?.address?.city),
                    readOnly: true,
                },
                {
                    key: 'state',
                    label: 'State',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(patient?.address?.state),
                    readOnly: true,
                },
                {
                    key: 'zip',
                    label: 'Zip',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(patient?.address?.zip),
                    readOnly: true,
                },
            ]
            const subscriberData: DataEntry[] = [
                {
                    key: 'relationShip',
                    label: 'RelationShip',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(
                        patient?.relationshipToSubscriber ??
                            fromRelationshipCode(
                                getStringValueOrDefault(patient?.relationshipToSubscriberCode),
                            ),
                    ),

                    readOnly: true,
                },
                {
                    key: 'subscriberMemberId',
                    label: 'Subscriber Member Id',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(
                        patient?.subscriberId ?? patient?.policyGroupNumber,
                    ),
                    readOnly: true,
                },
                {
                    key: 'memberId',
                    label: 'MemberId',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(
                        patient?.memberId ?? patient?.subscriberId,
                    ),
                    readOnly: true,
                },
                {
                    key: 'groupNumber',
                    label: 'Plan / Group Number',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(plan?.groupNumber),
                    readOnly: true,
                },
                {
                    key: 'employerGroupName',
                    label: 'Employer Name',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(plan?.employerGroupName),
                    readOnly: true,
                },
            ]

            const accumulatorData: DataEntry[] = [
                {
                    key: 'outOfPocketIndividual',
                    label: 'Out of Pocket - Individual',
                    type: FormValueType.CURRENCY,
                    initialValue: getStringValueOrDefault(
                        plan?.maxOutOfPocket?.individual?.toString(),
                    ),
                    readOnly: true,
                },
                {
                    key: 'outOfPocketFamily',
                    label: 'Out of Pocket - Family',
                    type: FormValueType.CURRENCY,
                    initialValue: getStringValueOrDefault(plan?.maxOutOfPocket?.family?.toString()),
                    readOnly: true,
                },
                {
                    key: 'deductibleSpendIndividual',
                    label: 'Deductible Spend Individual',
                    type: FormValueType.CURRENCY,
                    initialValue: getStringValueOrDefault(plan?.deductible.individual?.toString()),
                    readOnly: true,
                },
                {
                    key: 'deductibleSpendFamily',
                    type: FormValueType.CURRENCY,
                    label: 'Deductible Spend - Family',
                    initialValue: getStringValueOrDefault(plan?.deductible.family?.toString()),
                    readOnly: true,
                },
                {
                    key: 'totalPlanSpend',
                    label: 'Total Plan Spend',
                    type: FormValueType.CURRENCY,
                    initialValue: getStringValueOrDefault(plan?.totalMaximum?.toString()),
                    readOnly: true,
                },
                {
                    key: 'coveredPreventiveVisitCountRemaining',
                    type: FormValueType.NUMBER,
                    label: 'Covered Preventive Visit Remaining',
                    initialValue: (patient?.accumulators ?? [])
                        .filter((b: any) => b.category === 'preventive')
                        ?.reduce((n: number, item: any) => n + item.totalRemaining, 0),
                    readOnly: true,
                },
            ]
            const productBenefitData: DataEntry[] = [
                {
                    key: 'maxOutOfPocketIndividual',
                    label: 'Max OOP Individual',
                    type: FormValueType.CURRENCY,
                    initialValue: 0,
                    readOnly: true,
                },
                {
                    key: 'maxOutOfPocketFamily',
                    label: 'Max OOP Family',
                    type: FormValueType.CURRENCY,
                    initialValue: 0,
                    readOnly: true,
                },
                {
                    key: 'deductibleIndividual',
                    label: 'Deductible - Individual',
                    type: FormValueType.CURRENCY,
                    initialValue: getStringValueOrDefault(plan?.deductible.individual?.toString()),
                    readOnly: true,
                },
                {
                    key: 'deductibleFamily',
                    type: FormValueType.CURRENCY,
                    label: 'Deductible - Family',
                    initialValue: getStringValueOrDefault(plan?.deductible.family?.toString()),
                    readOnly: true,
                },
                {
                    key: 'totalPlanSpendRemaining',
                    label: 'Total Plan Spend Remaining',
                    type: FormValueType.CURRENCY,
                    initialValue: getStringValueOrDefault(plan?.totalMaximum?.toString()),
                    readOnly: true,
                },
                {
                    key: 'coveredPreventiveVisitCount',
                    type: FormValueType.NUMBER,
                    label: 'Covered Preventive Visit Count',
                    initialValue: (plan?.benefits ?? [])
                        .filter((b: any) => b.category === 'preventive')
                        ?.reduce((n: number, item: any) => n + item.totalServices, 0),
                    readOnly: true,
                },
                {
                    key: 'currentPlanYear',
                    type: FormValueType.NUMBER,
                    label: 'Current Plan Year',
                    initialValue: plan?.planYear,
                    readOnly: true,
                },
            ]

            const productData: DataEntry[] = [
                {
                    key: 'planYear',
                    label: 'Plan Year',
                    type: FormValueType.NUMBER,
                    initialValue: plan?.planYear,
                    readOnly: true,
                },
                {
                    key: 'name',
                    label: 'Name',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(plan?.planName),
                    readOnly: true,
                },
                {
                    key: 'networkName',
                    label: 'Network Name',
                    type: FormValueType.TEXT,
                    initialValue: getStringValueOrDefault(plan?.networkName),
                    readOnly: true,
                },
                {
                    key: 'payerName',
                    type: FormValueType.TEXT,
                    label: 'Payer',
                    initialValue: getStringValueOrDefault(plan?.payerName),
                    readOnly: true,
                },
            ]
            const coverageStatusData: DataEntry[] = [
                {
                    key: 'coverageEffectiveStartDate',
                    label: 'Start Date',
                    type: FormValueType.DATE,
                    initialValue: patient?.coverageStartDate,
                    readOnly: true,
                },
                {
                    key: 'coverageEffectiveEndDate',
                    label: 'End Date',
                    type: FormValueType.DATE,
                    initialValue: patient?.coverageEndDate,
                    readOnly: true,
                },
                {
                    key: 'active',
                    label: 'Active Status',
                    type: FormValueType.SWITCH,
                    initialValue: patient?.isActive ?? false,
                    readOnly: true,
                },
            ]
            setFormData({
                demographics: demographicsData,
                address: addressData,
                subscriber: subscriberData,
                accumulator: accumulatorData,
                productBenefits: productBenefitData,
                product: productData,
                coverageStatus: coverageStatusData,
                transactionLogs: transactionLogsData,
                recordDetails: recordDetailsData,
            })
        }
        fetchAndPopulate()
    }, [props])

    const copyToClipboard = (value: string) => {
        navigator.clipboard.writeText(value)
    }

    const handleFormChange = (formName: string) => (update: FormResult) => {
        const newState = { ...formResults, [formName]: update }
        const stateIsPopulated = Object.keys(newState).reduce((acc, key) => {
            if (acc) return true
            return Object.keys(newState[key]).length > 0
        }, false)
        setSubmitDisabled(!stateIsPopulated)
        setFormResults(newState)
    }

    const buildLogs = (data: any[]) => {
        let value = ''
        data.map((m) => {
            value += `${dayjs(m.timeStamp as number).format(
                'MM/DD/YYYY hh:mm:ss',
            )}: ${m.message?.substring(0, 40)}... \n`
            return value
        })
        return value
    }
    const renderTextControl = (entry: DataEntry) => {
        const sx = {
            margin: 1,
        }
        const value = entry.initialValue
        return (
            <TextField
                InputProps={entry?.options?.props}
                fullWidth={entry?.options?.fullWidth}
                sx={sx}
                key={entry.key}
                label={entry.label}
                value={
                    entry.type === FormValueType.DATE
                        ? dayjs(value as number).format('MM/DD/YYYY hh:mm:ss:SSS')
                        : value
                }
                disabled={entry.readOnly}
                rows={entry?.options?.rows ?? undefined}
                multiline={entry.options?.multiline}
            />
        )
    }
    const renderFormSection = (
        name:
            | 'demographics'
            | 'address'
            | 'subscriber'
            | 'accumulator'
            | 'productBenefits'
            | 'product'
            | 'coverageStatus'
            | 'transactionLogs',
        title?: string,
        sx?: SxProps<Theme>,
    ) => (
        <Box sx={sx ? { ...sx } : { margin: 1 }}>
            {title && (
                <DataPanel
                    title={title}
                    content={
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <OneStepForm
                                    formData={formData[name]}
                                    controlled
                                    onChange={handleFormChange(name)}
                                />
                            </Grid>
                        </Grid>
                    }
                ></DataPanel>
            )}
        </Box>
    )

    return (
        <>
            <DentalProductDetailsDialog
                open={planDialogOpen}
                onClose={() => setPlanDialogOpen(false)}
                dentalProduct={planDialogData as DentalMarketingPlan}
            />
            <Dialog open={open} maxWidth="xl">
                <DialogTitle sx={{ marginLeft: 2, fontSize: '3rem' }}>
                    Eligibility Details
                </DialogTitle>
                <DialogContent sx={{ margin: 2 }}>
                    <Stack spacing={1}>
                        <Grid container spacing={2}>
                            <Grid item sx={{ display: 'flex' }}>
                                <Grid item xs={8} sx={{ marginBottom: 1 }}>
                                    {renderFormSection('demographics', 'Demographics')}
                                    {renderFormSection('address', 'Address')}
                                    <Box sx={{ margin: 1 }}>
                                        <DataPanel
                                            title={'Primary Subscriber'}
                                            content={
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <OneStepForm
                                                            formData={formData['subscriber']}
                                                            controlled
                                                            onChange={handleFormChange(
                                                                'subscriber',
                                                            )}
                                                        />
                                                    </Grid>
                                                    {fromRelationshipCode(
                                                        patient?.relationshipToSubscriberCode ??
                                                            '18',
                                                    )?.toUpperCase() !== 'SELF' &&
                                                        currentNode === 'payer' && (
                                                            <Grid
                                                                container
                                                                justifyContent={'flex-end'}
                                                            >
                                                                <Button
                                                                    variant="text"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            'transparent',
                                                                    }}
                                                                    disableElevation={true}
                                                                    endIcon={<ContentCopy />}
                                                                    onClick={() => {
                                                                        onMemberClick(
                                                                            patient?.subscriberId ??
                                                                                patient?.policyGroupNumber,
                                                                            patient?.coverageEffectiveStartDate
                                                                                ? new Date(
                                                                                      patient?.coverageEffectiveStartDate,
                                                                                  ).getFullYear()
                                                                                : new Date().getFullYear(),
                                                                        )
                                                                    }}
                                                                >
                                                                    View Record
                                                                </Button>
                                                            </Grid>
                                                        )}
                                                </Grid>
                                            }
                                        ></DataPanel>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            height: '100%',
                                            margin: 1,
                                            marginLeft: 0,
                                            marginBottom: 1,
                                        }}
                                    >
                                        <DataPanel
                                            title={'Record Details'}
                                            content={
                                                <Grid container direction="column" spacing={2}>
                                                    {formData['recordDetails']?.map((entry) => {
                                                        return (
                                                            <Grid item key={entry.key}>
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={2}
                                                                    sx={{
                                                                        padding: 1,
                                                                        alignItems: 'center',
                                                                        justifyContent:
                                                                            'space-between',
                                                                    }}
                                                                >
                                                                    <Typography>
                                                                        {entry.label}
                                                                    </Typography>
                                                                    {renderTextControl(entry)}
                                                                </Stack>
                                                            </Grid>
                                                        )
                                                    })}
                                                    <Box
                                                        sx={{
                                                            margin: 2,
                                                            display: 'flex',
                                                            justifyContent: 'flex-end',
                                                        }}
                                                    >
                                                        <Button
                                                            variant="text"
                                                            sx={{
                                                                backgroundColor: 'transparent',
                                                            }}
                                                            disableElevation={true}
                                                            endIcon={<ContentCopy />}
                                                            onClick={() => {
                                                                copyToClipboard(
                                                                    JSON.stringify(
                                                                        data.requestContent,
                                                                    ),
                                                                )
                                                            }}
                                                        >
                                                            Request
                                                        </Button>
                                                        <Button
                                                            variant="text"
                                                            sx={{
                                                                backgroundColor: 'transparent',
                                                            }}
                                                            disableElevation={true}
                                                            endIcon={<ContentCopy />}
                                                            onClick={() => {
                                                                copyToClipboard(
                                                                    JSON.stringify(
                                                                        data.responseContent,
                                                                    ),
                                                                )
                                                            }}
                                                        >
                                                            Response
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                            }
                                        ></DataPanel>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                {renderFormSection('accumulator', 'Accumulators', {
                                    marginTop: 0,
                                    marginLeft: 1,
                                    marginRight: 1,
                                })}
                                {renderFormSection('productBenefits', 'Product Benefit Reference')}
                                <Box sx={{ display: 'flex' }}>
                                    <Box sx={{ marginTop: 0, marginBottom: 0, marginLeft: 1 }}>
                                        <DataPanel
                                            title={'Product Reference'}
                                            content={
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <OneStepForm
                                                            formData={formData['product']}
                                                            controlled
                                                            onChange={handleFormChange('product')}
                                                        />
                                                    </Grid>
                                                    {currentNode === 'payer' && (
                                                        <Grid container justifyContent={'flex-end'}>
                                                            <Button
                                                                variant="text"
                                                                sx={{
                                                                    backgroundColor: 'transparent',
                                                                }}
                                                                disableElevation={true}
                                                                onClick={() => {
                                                                    setPlanDialogOpen(true)
                                                                }}
                                                            >
                                                                More Details
                                                            </Button>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            }
                                        ></DataPanel>
                                    </Box>
                                    {renderFormSection('coverageStatus', 'Coverage Status', {
                                        marginTop: 0,
                                        marginBottom: 0,
                                        marginLeft: 1,
                                        marginRight: 1,
                                    })}
                                </Box>
                                <Box sx={{ margin: 1 }}>
                                    <DataPanel
                                        title={'Transaction Details'}
                                        content={
                                            <Grid container spacing={1}>
                                                <Grid spacing={2} item xs={2} sm={4} md={8}>
                                                    {formData['transactionLogs'].map((entry) => {
                                                        entry.options = { width: '25%' }
                                                        return renderTextControl(entry)
                                                    })}
                                                </Grid>
                                                <Grid spacing={2} item md={4}>
                                                    {renderTextControl({
                                                        key: 'details',
                                                        label: 'Details',
                                                        type: FormValueType.TEXT,
                                                        initialValue: buildLogs(data?.trace ?? []),
                                                        readOnly: true,
                                                        options: {
                                                            rows: 6,
                                                            multiline: true,
                                                            fullWidth: true,
                                                            props: {
                                                                style: { fontSize: '0.75rem' },
                                                            },
                                                        },
                                                    })}
                                                </Grid>
                                            </Grid>
                                        }
                                    ></DataPanel>
                                </Box>
                            </Grid>
                        </Grid>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button disabled={submitDisabled} onClick={() => {}}>
                        Validate
                    </Button>
                    <Button disabled={submitDisabled} onClick={() => {}}>
                        Update
                    </Button>
                    <Button onClick={() => onClose()}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default EligibilityDetailsDialog
